/**
 * Coverage Card Component
 * Displays coverage details for a single group
 */

import { useMemo, useState } from 'react';
import { useLingui } from '@lingui/react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CoverageStatusBadge } from './CoverageStatusBadge';
import { cn } from '@/lib/utils';
import { formatDecimal, formatPercent } from '@/lib/format';
import type { GroupCoverage } from '@/types/coverage';
import { Users, UserCheck, Info, AlertTriangle } from 'lucide-react';
import { CapacityOverviewDialog } from '@/components/capacity/CapacityOverviewDialog';
import { StaffListDialog } from './StaffListDialog';

export interface CoverageCardProps {
  /** Group coverage data */
  group: GroupCoverage;
  /** The date in YYYY-MM-DD format */
  date: string;
  /** Called when clicking a staff member — navigate to shift planning */
  onStaffClick?: (employeeId: string) => void;
  /** Optional additional class names */
  className?: string;
}

/**
 * Displays coverage information for a single group including:
 * - Group name with color indicator
 * - Children count (raw and weighted)
 * - Staff: Required vs Scheduled
 * - Coverage percentage
 * - Coverage status badge
 */
export function CoverageCard({ group, date, onStaffClick, className }: CoverageCardProps) {
  const { i18n } = useLingui();
  const [showOverview, setShowOverview] = useState(false);
  const [showStaff, setShowStaff] = useState(false);

  // An organisation that never saved a staffing model has no requirement to be
  // measured against: required_staff and coverage_percent are left at zero by the
  // service, so the card claims no figure and no understaffing for it (#1509).
  const isNotConfigured = group.status === 'NOT_CONFIGURED';

  // Format coverage percentage for display
  const coveragePercentDisplay = useMemo(() => {
    if (isNotConfigured) return '—';
    const percent = group.coverage_percent ?? 0;
    return formatPercent(percent / 100, i18n.locale, 0);
  }, [group.coverage_percent, isNotConfigured, i18n.locale]);

  // Determine if coverage is under 100%
  const isUnderstaffed = useMemo(() => {
    if (isNotConfigured) return false;
    return (group.coverage_percent ?? 0) < 100;
  }, [group.coverage_percent, isNotConfigured]);

  return (
    <Card className={cn('relative overflow-hidden', className)}>
      {/* Color bar indicator at top */}
      <div
        className={cn(
          'absolute top-0 left-0 right-0 h-1',
          group.status === 'SUFFICIENT'
            ? 'bg-success'
            : group.status === 'WARNING'
              ? 'bg-warning'
              : isNotConfigured
                ? 'bg-muted-foreground/40'
                : 'bg-destructive'
        )}
        aria-hidden="true"
      />

      <CardHeader className="pb-2 pt-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold">
            {group.group_name || i18n._({ id: 'common.unknown', message: 'Unknown' })}
          </CardTitle>
          <CoverageStatusBadge status={group.status} />
        </div>
        {group.group_type && (
          <p className="text-sm text-muted-foreground">{group.group_type}</p>
        )}
      </CardHeader>

      <CardContent className="space-y-3">
        {/* Children count - clickable to show capacity overview */}
        <button
          type="button"
          className="flex items-center justify-between w-full rounded-md px-2 py-1 -mx-2 hover:bg-accent transition-colors cursor-pointer"
          onClick={() => setShowOverview(true)}
        >
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Users className="h-4 w-4" aria-hidden="true" />
            <span>
              {i18n._({ id: 'coverage.card.children', message: 'Children' })}
            </span>
          </div>
          <div className="text-right">
            <span className="font-medium">
              {group.weighted_child_count !== undefined
                ? formatDecimal(group.weighted_child_count, i18n.locale, 1)
                : (group.child_count ?? 0)}
            </span>
            {group.baby_count !== undefined && group.baby_count > 0 && (
              <span className="text-sm text-muted-foreground ml-2">
                {i18n._({
                  id: 'coverage.card.babiesAndChildren',
                  message: '({babies, plural, one {# baby} other {# babies}}, {children, plural, one {# child} other {# children}})',
                  values: {
                    babies: group.baby_count,
                    children: group.non_baby_count ?? (group.child_count ?? 0) - group.baby_count,
                  },
                })}
              </span>
            )}
          </div>
        </button>

        {/* Staff coverage - clickable to show staff list */}
        <button
          type="button"
          className="flex items-center justify-between w-full rounded-md px-2 py-1 -mx-2 hover:bg-accent transition-colors cursor-pointer"
          onClick={() => setShowStaff(true)}
        >
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <UserCheck className="h-4 w-4" aria-hidden="true" />
            <span>
              {i18n._({ id: 'coverage.card.staff', message: 'Staff' })}
            </span>
          </div>
          <div className="text-right">
            <span
              className={cn(
                'font-medium',
                isUnderstaffed && 'text-destructive'
              )}
            >
              {group.scheduled_staff ?? 0}
            </span>
            <span className="text-muted-foreground mx-1">/</span>
            <span className="text-muted-foreground">
              {isNotConfigured ? '—' : Math.round(group.required_staff ?? 0)}
            </span>
            <span className="text-sm text-muted-foreground ml-1">
              {i18n._({ id: 'coverage.card.required', message: 'required' })}
            </span>
          </div>
        </button>

        {/* Qualified staff count + shortfall warning */}
        {group.min_qualified_staff !== undefined && group.min_qualified_staff > 0 && (
          <div
            className={cn(
              'flex items-center gap-1.5 text-xs px-2 -mt-1',
              group.qualified_shortfall ? 'text-destructive' : 'text-muted-foreground'
            )}
          >
            {group.qualified_shortfall ? (
              <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden="true" />
            ) : (
              <Info className="h-3 w-3 shrink-0" aria-hidden="true" />
            )}
            <span className="font-medium">
              {i18n._({
                id: 'coverage.card.qualifiedCount',
                message: '{scheduled}/{min} qualified',
                values: {
                  scheduled: group.scheduled_qualified_staff ?? 0,
                  min: group.min_qualified_staff,
                },
              })}
            </span>
            {group.qualified_shortfall && (
              <span>
                {i18n._({
                  id: 'coverage.card.qualifiedShortfall',
                  message: '— understaffed (qualified)',
                })}
              </span>
            )}
          </div>
        )}

        {/* Coverage percentage */}
        <div className="pt-2 border-t">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">
              {i18n._({
                id: 'coverage.card.coveragePercent',
                message: 'Coverage',
              })}
            </span>
            <span
              className={cn(
                'text-2xl font-bold',
                group.status === 'SUFFICIENT' && 'text-success-on-surface',
                group.status === 'WARNING' && 'text-warning-on-surface',
                group.status === 'CRITICAL' && 'text-destructive-on-surface'
              )}
            >
              {coveragePercentDisplay}
            </span>
          </div>

          {/* Progress bar */}
          <div className="mt-2 h-2 bg-muted rounded-full overflow-hidden">
            <div
              className={cn(
                'h-full transition-all duration-300',
                group.status === 'SUFFICIENT' && 'bg-success',
                group.status === 'WARNING' && 'bg-warning',
                group.status === 'CRITICAL' && 'bg-destructive'
              )}
              style={{
                width: isNotConfigured
                  ? '0%'
                  : `${Math.min(100, group.coverage_percent ?? 0)}%`,
              }}
            />
          </div>
        </div>
      </CardContent>

      {/* Capacity Overview Dialog - rendered only when open to avoid unnecessary hook calls */}
      {showOverview && group.group_id && (
        <CapacityOverviewDialog
          open={showOverview}
          onOpenChange={setShowOverview}
          groupId={group.group_id}
          groupName={group.group_name}
          date={date}
        />
      )}

      {/* Staff List Dialog */}
      {showStaff && group.group_id && (
        <StaffListDialog
          open={showStaff}
          onOpenChange={setShowStaff}
          groupId={group.group_id}
          date={date}
          onStaffClick={onStaffClick}
        />
      )}
    </Card>
  );
}
